import { useSyncExternalStore } from "react";
import { createListeners } from "@lattice-php/ui/lib/listeners";

const appearances = ["light", "dark", "system"] as const;

export type Appearance = (typeof appearances)[number];

export type ResolvedAppearance = "light" | "dark";

export type UseAppearanceReturn = {
  readonly appearance: Appearance;
  readonly resolvedAppearance: ResolvedAppearance;
  readonly updateAppearance: (mode: Appearance) => void;
};

const { subscribe, notify } = createListeners();
let currentAppearance: Appearance = "system";
let serverAppearance: Appearance = "system";

/**
 * Seed the SSR/hydration snapshot from the backend-shared `lattice.appearance`
 * prop. The server render and the client's hydration pass must read the same
 * value, or components branching on the appearance mismatch on hydration.
 */
export function seedAppearance(value: unknown): void {
  if (isAppearance(value)) {
    serverAppearance = value;
  }
}

export function isAppearance(value: unknown): value is Appearance {
  return appearances.some((appearance) => appearance === value);
}

const prefersDark = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const setCookie = (name: string, value: string, days = 365): void => {
  if (typeof document === "undefined") {
    return;
  }

  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const getStoredAppearance = (): Appearance => {
  if (typeof window === "undefined") {
    return "system";
  }

  const stored = localStorage.getItem("appearance");

  return isAppearance(stored) ? stored : "system";
};

const isDarkMode = (appearance: Appearance): boolean => {
  return appearance === "dark" || (appearance === "system" && prefersDark());
};

const getSystemAppearance = (): ResolvedAppearance => (prefersDark() ? "dark" : "light");

const applyAppearance = (appearance: Appearance): void => {
  if (typeof document === "undefined") {
    return;
  }

  const isDark = isDarkMode(appearance);

  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
};

const mediaQuery = (): MediaQueryList | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.matchMedia("(prefers-color-scheme: dark)");
};

const handleSystemAppearanceChange = (): void => {
  applyAppearance(currentAppearance);
  notify();
};

export function updateAppearance(mode: Appearance): void {
  currentAppearance = mode;

  localStorage.setItem("appearance", mode);

  setCookie("appearance", mode);

  applyAppearance(mode);
  notify();
}

export function initializeAppearance(): void {
  if (typeof window === "undefined") {
    return;
  }

  if (!localStorage.getItem("appearance")) {
    localStorage.setItem("appearance", "system");
    setCookie("appearance", "system");
  }

  currentAppearance = getStoredAppearance();
  applyAppearance(currentAppearance);

  mediaQuery()?.addEventListener("change", handleSystemAppearanceChange);
}

export function useAppearance(): UseAppearanceReturn {
  const appearance: Appearance = useSyncExternalStore(
    subscribe,
    () => currentAppearance,
    () => serverAppearance,
  );
  const systemAppearance: ResolvedAppearance = useSyncExternalStore(
    subscribe,
    getSystemAppearance,
    () => "light",
  );

  const resolvedAppearance: ResolvedAppearance =
    appearance === "system" ? systemAppearance : appearance;

  return { appearance, resolvedAppearance, updateAppearance } as const;
}
